import React, { useState, useEffect } from "react";
import api from "../services/api";
import { AlertTriangle, Calendar, Download, Info } from "lucide-react";

export default function TaxReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get("/admin/tax-report");
      setReports(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilDue = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Due on the 20th of the current month (for last month's taxes)
    let dueDay = new Date(currentYear, currentMonth, 20);
    
    if (today > dueDay) {
        // If already passed the 20th, the next due date is the 20th of next month
        dueDay = new Date(currentYear, currentMonth + 1, 20);
    }
    
    const diffTime = Math.abs(dueDay - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { days: diffDays, date: dueDay.toLocaleDateString() };
  };

  const nextDue = getDaysUntilDue();
  
  const handleExportCSV = () => {
    if (reports.length === 0) return;

    const headers = ["Period", "Orders", "Net Sales", "SC State Tax (6%)", "County Tax (1%/3%)", "Hospitality Tax (2%)", "Total Tax"];
    const csvRows = [headers.join(",")];

    reports.forEach(r => {
      const scdorTotal = r.scStateTax + r.dorchesterCountyTax + r.summervilleLocalTax;
      const row = [
        r.period,
        r.orderCount,
        r.netSales.toFixed(2),
        r.scStateTax.toFixed(2),
        (r.dorchesterCountyTax + r.summervilleLocalTax).toFixed(2),
        r.summervilleHospitalityTax.toFixed(2),
        (scdorTotal + r.summervilleHospitalityTax).toFixed(2)
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `raya_tax_reconciliation_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="p-12 text-center text-raya-text-secondary">Generating tax reports...</div>;
  if (error) return <div className="p-12 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header & Reminder */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-raya-text-primary tracking-tight">Tax Reporting & Compliance</h2>
          <p className="text-raya-text-secondary mt-1">Aggregated totals for SC State and Dorchester/Summerville filings.</p>
        </div>

        <div className={`p-4 rounded-2xl flex items-center gap-4 border shadow-sm ${
          nextDue.days <= 5 ? 'bg-red-50 border-red-100 text-red-900' : 'bg-raya-accent/10 border-raya-accent/20 text-raya-text-primary'
        }`}>
          <div className={`p-2 rounded-xl ${nextDue.days <= 5 ? 'bg-red-500 text-white' : 'bg-raya-accent text-black'}`}>
            <Calendar size={24} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest opacity-70">Next Filing Deadline</p>
            <p className="text-lg font-black">{nextDue.date} <span className="text-sm font-medium">({nextDue.days} days left)</span></p>
          </div>
        </div>
      </div>

      {/* Filing Portals Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 border border-raya-border shadow-sm flex gap-4">
          <div className="h-12 w-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 shrink-0">
            <Info size={24} />
          </div>
          <div>
            <h4 className="font-bold text-lg mb-2">SC Sales Tax (7.0%)</h4>
            <p className="text-sm text-raya-text-secondary mb-4">File via <span className="font-bold text-blue-600 underline">MyDORWAY</span>. Includes State (6%) and Dorchester County (1%).</p>
            <a href="https://mydorway.dor.sc.gov/" target="_blank" rel="noreferrer" className="text-xs font-bold text-raya-accent uppercase tracking-widest hover:underline">Open Portal →</a>
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 border border-raya-border shadow-sm flex gap-4">
          <div className="h-12 w-12 rounded-2xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 shrink-0">
            <AlertTriangle size={24} />
          </div>
          <div>
            <h4 className="font-bold text-lg mb-2">Hospitality Tax (2.0%)</h4>
            <p className="text-sm text-raya-text-secondary mb-4">File via <span className="font-bold text-orange-600 underline">CitizenServe</span>. Required for all prepared meals and beverages.</p>
            <a href="https://www.citizenserve.com/summerville" target="_blank" rel="noreferrer" className="text-xs font-bold text-raya-accent uppercase tracking-widest hover:underline">Open Portal →</a>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-raya-card rounded-3xl shadow-xl border border-raya-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-raya-bg-off border-b border-raya-border">
              <tr>
                <th className="p-6 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">Filing Period</th>
                <th className="p-6 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary text-right">Net Sales</th>
                <th className="p-6 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary text-right">SCDOR (7%) Total</th>
                <th className="p-6 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary text-right">Summerville Hosp (2%)</th>
                <th className="p-6 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary text-right font-black">Total Tax Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-raya-border">
              {reports.map((report, idx) => {
                const scdorTotal = report.scStateTax + report.dorchesterCountyTax + report.summervilleLocalTax;
                return (
                  <tr key={idx} className="hover:bg-raya-accent/5 transition-colors">
                    <td className="p-6">
                      <div className="font-black text-lg text-raya-text-primary uppercase tracking-tight">
                        {new Date(report.period + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                      <div className="text-xs text-raya-text-tertiary font-medium">{report.orderCount} orders processed</div>
                    </td>
                    <td className="p-6 text-right font-medium text-raya-text-secondary font-mono">
                      ${report.netSales.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="p-6 text-right font-mono">
                      <div className="font-bold text-raya-text-primary">${scdorTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div className="text-[10px] text-raya-text-tertiary">SC (6%) + County (1%/3%)</div>
                    </td>
                    <td className="p-6 text-right font-mono text-orange-600 bg-orange-50/10">
                      <div className="font-bold">${report.summervilleHospitalityTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div className="text-[10px] opacity-70">Prepared Meals Only</div>
                    </td>
                    <td className="p-6 text-right border-l border-raya-border bg-raya-accent/5">
                      <div className="text-xl font-black text-raya-accent font-mono">
                        ${(scdorTotal + report.summervilleHospitalityTax).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {reports.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-20 text-center text-raya-text-tertiary italic">
                    No tax data available for the current period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Detailed Download Box */}
      <div className="bg-raya-accent rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between shadow-2xl shadow-raya-accent/20">
        <div className="text-center md:text-left mb-6 md:mb-0">
          <h3 className="text-2xl font-black text-black">Ready to File?</h3>
          <p className="text-black/70 font-medium">Download the itemized CSV to upload directly to your accounting software.</p>
        </div>
        <button 
          onClick={handleExportCSV}
          className="bg-black text-white px-8 py-4 rounded-2xl font-black flex items-center gap-3 hover:scale-105 transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={reports.length === 0}
        >
          <Download size={20} />
          EXPORT RECONCILIATION
        </button>
      </div>
    </div>
  );
}
